// PWA-detektering + installasjons-prompt-håndtering.
// useIsPWA() → true når appen kjører som installert PWA (standalone-modus)
// useInstallPrompt() → { canInstall, promptInstall } — for å vise "Installer"-knapp

import { useState, useEffect } from "react";

/**
 * Detekter om appen kjører som installert PWA (standalone-modus).
 * Fungerer på både iOS og Android.
 */
export function useIsPWA() {
  const [isPWA, setIsPWA] = useState(() => detectPWA());

  useEffect(() => {
    const mq = window.matchMedia("(display-mode: standalone)");
    const onChange = () => setIsPWA(detectPWA());
    mq.addEventListener?.("change", onChange);
    return () => mq.removeEventListener?.("change", onChange);
  }, []);

  return isPWA;
}

function detectPWA() {
  if (typeof window === "undefined") return false;
  // Standard media query
  if (window.matchMedia?.("(display-mode: standalone)").matches) return true;
  // iOS Safari legacy
  if (window.navigator?.standalone === true) return true;
  // Android TWA
  if (document.referrer?.startsWith("android-app://")) return true;
  return false;
}

/**
 * Fanger opp beforeinstallprompt-eventet (Android/desktop) og eksponerer
 * en promptInstall()-funksjon som kan trigges fra en knapp.
 *
 * NB: iOS Safari støtter IKKE beforeinstallprompt — brukeren må selv
 * bruke Del-menyen → "Legg til på Hjem-skjerm". useIsIOSMobile() hjelper
 * med å vise iOS-spesifikk instruksjon.
 */
export function useInstallPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [installed, setInstalled] = useState(false);

  useEffect(() => {
    const onBeforeInstall = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };
    const onInstalled = () => {
      setInstalled(true);
      setDeferredPrompt(null);
    };
    window.addEventListener("beforeinstallprompt", onBeforeInstall);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onBeforeInstall);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  const promptInstall = async () => {
    if (!deferredPrompt) return { outcome: "unsupported" };
    deferredPrompt.prompt();
    const choice = await deferredPrompt.userChoice;
    setDeferredPrompt(null);
    return choice; // { outcome: 'accepted' | 'dismissed' }
  };

  return {
    canInstall: !!deferredPrompt && !installed,
    installed,
    promptInstall,
  };
}

/**
 * Detekter iOS Safari (mobil). Brukes for å vise "trykk Del → Legg til på
 * Hjem-skjerm"-instruksjon siden iOS ikke støtter beforeinstallprompt.
 */
export function useIsIOSMobile() {
  const [isIOS, setIsIOS] = useState(false);
  useEffect(() => {
    const ua = window.navigator.userAgent || "";
    const iOS = /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
    // Ekskluder Chrome/Firefox på iOS (de har innebygd browser-chrome)
    const isSafari = /^((?!chrome|android).)*safari/i.test(ua);
    setIsIOS(iOS && isSafari);
  }, []);
  return isIOS;
}
